// assets/min-cart-location.js

// Minimum order values per location (₹)
const MINIMUMS_BY_LOCATION = {
  gurgaon: 1000,
  delhi: 1800,
  dwarka: 1000,
  noida: 1800,
  jaipur: 1900
}

const MINIMUMS_ONE_DAY_BY_LOCATION = {
  gurgaon: 3000,
  // other locations don't allow 1-day so no entry needed
}

/**
 * Adds N days to a date string like "16 Jun 26" or "16 Jun 2026"
 * Returns the new date string in the same format
 */
function addDaysToDateString(dateStr, days = 1) {
  if (!dateStr) return dateStr

  // Matches "16 jun 26" or "16 Jun 2026" (case-insensitive after normalizeDate)
  const match = dateStr.trim().match(/^(\d{1,2})\s+([a-z]+)\s+(\d{2,4})$/i)
  if (!match) return dateStr

  let [, day, month, year] = match

  // Normalize 2-digit year → 4-digit
  if (year.length === 2) year = "20" + year

  // Map month name → 0-indexed number
  const monthMap = {
    jan: 0,
    feb: 1,
    mar: 2,
    apr: 3,
    may: 4,
    jun: 5,
    jul: 6,
    aug: 7,
    sep: 8,
    oct: 9,
    nov: 10,
    dec: 11,
  }
  const monthIndex = monthMap[month.toLowerCase().slice(0, 3)]
  if (monthIndex === undefined) return dateStr

  // Manually construct Date to avoid browser parsing issues
  const date = new Date(parseInt(year), monthIndex, parseInt(day))
  date.setDate(date.getDate() + days)

  const newDay = String(date.getDate()).padStart(2, "0")
  const monthNames = [
    "jan",
    "feb",
    "mar",
    "apr",
    "may",
    "jun",
    "jul",
    "aug",
    "sep",
    "oct",
    "nov",
    "dec",
  ]
  const newMonth = monthNames[date.getMonth()]
  const newYear = String(date.getFullYear()).slice(-2)

  return `${newDay} ${newMonth} ${newYear}`
}

function getMinimumOrderValue(location, isOneDay = false) {
  if (isOneDay && MINIMUMS_ONE_DAY_BY_LOCATION[location] !== undefined) {
    return MINIMUMS_ONE_DAY_BY_LOCATION[location]
  }
  return MINIMUMS_BY_LOCATION[location] || 0
}

function capitalize(str) {
  if (!str) return str
  return str.charAt(0).toUpperCase() + str.slice(1)
}

function getDisplayLocation(location) {
  return location === "noida" ? "Noida / Faridabad / East Delhi" : capitalize(location)
}

function normalizeLocation(value = "") {
  const raw = value.toLowerCase().replace(/[\s-]+/g, "")

  if (
    raw.includes("newgurgaon/dwarkaexpressway") ||
    raw.includes("newgurgaondwarkaexpressway")
  ) {
    return "dwarka"
  }

  if (
    raw.includes("centraldelhi/westdelhi/southdelhi") ||
    raw.includes("centraldelhiwestdelhisouthdelhi")
  ) {
    return "delhi"
  }

  if (raw.includes("noida/faridabad/eastdelhi") || raw.includes("noidafaridabadeastdelhi")) {
    return "noida"
  }

  if (raw.includes("dwarka")) return "dwarka"
  if (raw.includes("gurgaon")) return "gurgaon"
  if (raw.includes("delhi")) return "delhi"
  if (raw.includes("jaipur")) return "jaipur"

  return ""
}

function normalizeDate(value = "") {
  return value.trim().toLowerCase().replace(/\s+/g, " ")
}

function parseMoney(text = "") {
  const clean = String(text).replace(/,/g, "")
  const match = clean.match(/\d+(\.\d+)?/)
  return match ? Math.round(parseFloat(match[0])) : 0
}

// fee product exclusion
function isExcludedRow(row) {
  const nameEl = row.querySelector(".cart-item__name")
  if (!nameEl) return false

  const name = nameEl.textContent.trim().toLowerCase()

  return (
    name.includes("worry free") ||
    name.includes("worry-free") ||
    name.includes("waiver") ||
    name.includes("worry free waiver")
  )
}

function getRowLineTotal(row) {
  const totalsContainer =
    row.querySelector(".cart-item__totals.right.small-hide") ||
    row.querySelector(".cart-item__totals")

  let totalSpan = null

  if (totalsContainer) {
    const prices = totalsContainer.querySelectorAll(".price--end")
    if (prices.length > 0) {
      totalSpan = prices[prices.length - 1]
    }
  }

  if (!totalSpan) {
    totalSpan =
      row.querySelector(".cart-item__totals.right.small-hide .price--end") ||
      row.querySelector(".cart-item__totals .price--end")
  }

  if (!totalSpan) return 0

  return parseMoney(totalSpan.textContent || "")
}

function parseRow(row) {
  if (isExcludedRow(row)) return null

  const detailsTd = row.querySelector(".cart-item__details")
  if (!detailsTd) return null

  const productOptions = detailsTd.querySelectorAll(".product-option")

  let locationKey = ""
  let startDate = ""
  let endDate = ""
  let genericDate = ""
  let timeSlot = ""
  let isOneDay = false

  productOptions.forEach((opt) => {
    const dt = opt.querySelector("dt")
    const dd = opt.querySelector("dd")
    if (!dt || !dd) return

    const label = dt.textContent.trim().toLowerCase()
    const value = dd.textContent.trim()

    if (label.includes("location")) {
      locationKey = normalizeLocation(value)
    }

    if (label.includes("start date")) {
      startDate = normalizeDate(value)
    }

    if (label.includes("end date")) {
      endDate = normalizeDate(value)
    }

    if (label === "date" || label.includes("date")) {
      genericDate = normalizeDate(value)
    }

    if (label.includes("time")) {
      timeSlot = normalizeDate(value)
    }

    // Detect 1 Day duration from cart line properties
    if (label.includes("days") || label === "days" || label.includes("duration")) {
      if (/^1\s*(day|days)$/i.test(value.trim())) isOneDay = true
    }
  })

  // 2. Check start/end date diff (1 calendar day apart = 1-day rental)
  if (!isOneDay && startDate && endDate) {
    const monthMap = { jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5, jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11 }

    function parseDateStr(str) {
      const m = str.trim().match(/^(\d{1,2})\s+([a-z]+)\s+(\d{2,4})$/i)
      if (!m) return null
      let [, d, mon, y] = m
      if (y.length === 2) y = "20" + y
      const mi = monthMap[mon.toLowerCase().slice(0, 3)]
      if (mi === undefined) return null
      return new Date(parseInt(y), mi, parseInt(d))
    }

    const sd = parseDateStr(startDate)
    const ed = parseDateStr(endDate)
    if (sd && ed) {
      const diffMs = ed - sd
      const diffDays = diffMs / (1000 * 60 * 60 * 24)
      if (diffDays === 1) isOneDay = true
    }
  }

  // 3. Fallback: scan all text in details for "1 Day" standalone
  if (!isOneDay) {
    const detailsText = detailsTd.textContent
    if (/\b1\s*day\b/i.test(detailsText)) isOneDay = true
  }

  const effectiveStartDate = startDate || genericDate

  if (!locationKey || !effectiveStartDate) return null

  // Uses discounted displayed line total from cart row.
  // This matches your rule: enforce minimum on actual earned amount.
  const displayedLineTotal = getRowLineTotal(row)

  // Increment end date by 1 day if needed
  const normalizedEndDate = normalizeDate(endDate)
  const normalizedStartDate = normalizeDate(startDate || genericDate)

  // Only bump if end date still equals start date (i.e., not yet incremented)
  const adjustedEndDate =
    normalizedEndDate === normalizedStartDate
      ? addDaysToDateString(normalizedEndDate, 1)
      : normalizedEndDate

  // Only update DOM if we actually changed it
  if (adjustedEndDate !== normalizedEndDate) {
    productOptions.forEach((opt) => {
      const dt = opt.querySelector("dt")
      const dd = opt.querySelector("dd")
      if (!dt || !dd) return
      if (dt.textContent.trim().toLowerCase().includes("end date")) {
        dd.textContent = adjustedEndDate
      }
    })
  }

  return {
    row,
    locationKey,
    startDate: effectiveStartDate,
    endDate,
    timeSlot,
    displayedLineTotal,
    displayedLineTotal,
    rentalValue: displayedLineTotal,
    isOneDay,
    // Business rule:
    // same location + same start date = same order
    // end date does not matter
    orderKey: `${locationKey}||${effectiveStartDate}`,
    endDate: adjustedEndDate,
  }
}

/**
 * Reads cart rows from DOM and enforces:
 * - minimum order value per location
 * - grouped by location + start date
 * - discounted totals count
 * - fee/deposit excluded
 *
 * Returns: { valid: boolean, message: string }
 */
function checkCartTotalFromDom() {
  const allRows = Array.from(document.querySelectorAll("tr.cart-item"))
  const parsedItems = allRows.map(parseRow).filter(Boolean)

  const groupedTotals = {}
  const errors = new Set()

  parsedItems.forEach((item) => {
    if (!groupedTotals[item.orderKey]) {
      groupedTotals[item.orderKey] = {
        location: item.locationKey,
        startDate: item.startDate,
        total: 0,
        items: [],
      }
    }

    // If any item in the group is 1-day, flag the whole group
    if (item.isOneDay) groupedTotals[item.orderKey].isOneDay = true

    groupedTotals[item.orderKey].total += item.rentalValue
    groupedTotals[item.orderKey].items.push(item)
  })

  let valid = true

  Object.values(groupedTotals).forEach((group) => {
    const minOrderValue = getMinimumOrderValue(group.location, group.isOneDay)
    if (group.total < minOrderValue) {
      errors.add(
        group.isOneDay
          ? `For 1 day rentals in ${getDisplayLocation(group.location)}, a minimum rental value of ₹${minOrderValue.toLocaleString()} applies ).`
          : `For ${getDisplayLocation(group.location)} deliveries, a minimum rental value of ₹${minOrderValue.toLocaleString()} applies per delivery date/location combination .`,
      )
      valid = false
    }
  })

  return {
    valid,
    message: errors.size > 0 ? Array.from(errors).join(" ") : "",
  }
}

document.addEventListener("DOMContentLoaded", () => {
  const checkoutBtn = document.querySelector("#checkout")
  if (!checkoutBtn) return

  let errorEl = document.querySelector("#min-cart-error")
  if (!errorEl) {
    errorEl = document.createElement("p")
    errorEl.id = "min-cart-error"
    checkoutBtn.parentNode.insertBefore(errorEl, checkoutBtn.nextSibling)
  }

  errorEl.style.color = "red"
  errorEl.style.marginTop = "0.5rem"
  errorEl.style.fontSize = "13px"

  const runCheck = (event) => {
    const { valid, message } = checkCartTotalFromDom()

    if (!valid && event) event.preventDefault()

    if (valid) {
      checkoutBtn.removeAttribute("disabled")
      errorEl.textContent = ""
    } else {
      checkoutBtn.setAttribute("disabled", "disabled")
      errorEl.innerHTML = message || "Please check cart items again."
    }
  }

  window.runMinCartCheck = runCheck

  runCheck()

  checkoutBtn.addEventListener("click", (event) => {
    const { valid, message } = checkCartTotalFromDom()

    if (!valid) {
      event.preventDefault()
      checkoutBtn.setAttribute("disabled", "disabled")
      errorEl.innerHTML = message || "Please check cart items again."
    } else {
      checkoutBtn.removeAttribute("disabled")
      errorEl.textContent = ""
    }
  })

  document.addEventListener("change", (e) => {
    if (e.target.matches(".quantity__input")) {
      setTimeout(() => {
        runCheck()
      }, 300)
    }
  })

  const cartForm = document.querySelector("form[action='/cart']")
  if (cartForm) {
    cartForm.addEventListener("change", () => {
      setTimeout(() => {
        runCheck()
      }, 300)
    })
  }

  document.addEventListener("click", (e) => {
    if (
      e.target.closest("cart-remove-button") ||
      e.target.closest('button[name="remove"]') ||
      e.target.closest("[data-index]") ||
      e.target.closest('a[href*="/cart/change"]')
    ) {
      setTimeout(() => {
        runCheck()
      }, 1000)
    }
  })

  document.addEventListener("shopify:section:load", (e) => {
    const sectionId = e.detail && e.detail.sectionId
    if (sectionId && sectionId.indexOf("main-cart-items") !== -1) {
      runCheck()
    }
  })

  document.addEventListener("cart:updated", () => {
    runCheck()
  })
})

// Observe Ajax / fetch calls to any /cart endpoint
if ("PerformanceObserver" in window) {
  const cartObserver = new PerformanceObserver((list) => {
    list.getEntries().forEach((entry) => {
      const isAjax = ["xmlhttprequest", "fetch"].includes(entry.initiatorType)
      const isCartEndpoint = /\/cart(\/|\.js|\?|$)/.test(entry.name)

      if (isAjax && isCartEndpoint) {
        setTimeout(() => {
          if (typeof window.runMinCartCheck === "function") {
            window.runMinCartCheck()
          }
        }, 600)
      }
    })
  })

  cartObserver.observe({ entryTypes: ["resource"] })
}